import { axiosInstance } from '../../utils/axios'
import type {
  MissingReadingsConfig,
  MissingReadingsSummary,
  MissingReadingsDay,
  DeviceMissingReadingsSeries,
  DaySequenceDetail,
} from '../../types/missingReadings'

interface ApiResponse<T> {
  success: boolean
  data: T
}

const BASE_URL = '/admin/missing-readings'

export const missingReadingsApi = {
  getConfig: async (): Promise<MissingReadingsConfig> => {
    const res = await axiosInstance.get<ApiResponse<MissingReadingsConfig>>(`${BASE_URL}/config`)
    return res.data.data
  },
  updateConfig: async (input: Partial<MissingReadingsConfig>): Promise<MissingReadingsConfig> => {
    const res = await axiosInstance.patch<ApiResponse<MissingReadingsConfig>>(`${BASE_URL}/config`, input)
    return res.data.data
  },
  /** Fleet-wide day-by-day summary + top devices, for the dashboard card. */
  /** `accountId` scopes the day series and device list; `null` is the
   *  "no account" bucket, `undefined` is every account. */
  getSummary: async (days: number, accountId?: number | null): Promise<MissingReadingsSummary> => {
    const res = await axiosInstance.get<ApiResponse<MissingReadingsSummary>>(`${BASE_URL}/summary`, {
      params: {
        days,
        ...(accountId === undefined ? {} : { accountId: accountId === null ? 'none' : accountId }),
      },
    })
    return res.data.data
  },
  /** Per-device breakdown for one selected day — the fleet day page. */
  getDay: async (date: string, accountId?: number | null): Promise<MissingReadingsDay> => {
    const res = await axiosInstance.get<ApiResponse<MissingReadingsDay>>(`${BASE_URL}/day/${date}`, {
      params:
        accountId === undefined ? {} : { accountId: accountId === null ? 'none' : accountId },
    })
    return res.data.data
  },
  /** One device's day-by-day series — the selected-RIC panel. */
  getDeviceSeries: async (deviceId: number, days: number): Promise<DeviceMissingReadingsSeries> => {
    const res = await axiosInstance.get<ApiResponse<DeviceMissingReadingsSeries>>(`${BASE_URL}/device/${deviceId}`, {
      params: { days },
    })
    return res.data.data
  },
  /** Sequence-level detail for one device on one day — the day-row drill-in. */
  getDaySequence: async (deviceId: number, date: string): Promise<DaySequenceDetail> => {
    const res = await axiosInstance.get<ApiResponse<DaySequenceDetail>>(
      `${BASE_URL}/device/${deviceId}/day/${date}`
    )
    return res.data.data
  },
}
